/*
 * @TODO This view and all its children are preferably refactored in line with, /
 * using the components used in, the RdwStockView. The RdwStockView is pretty
 * stateless: deriving props from the querystring in the url, and setting
 * state by updating the url. That's a much easier solution than all the state
 * used in this view and it's children and therefore should be implemented /
 * reused in here to (since the two views are practically the same with some
 * small differences
 */

import PropTypes from 'prop-types'
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, withRouter } from 'react-router-dom'
import styled from 'styled-components/macro'
import useDeepCompareEffect from 'use-deep-compare-effect'

import { media } from 'utilities/styled'
import {
  getObjectFromQueryString,
  handleChangeQuery,
  scrollToTop,
} from 'utilities/utils'

import {
  CarFileStatusEnumLowerCase,
  CarFileStatusEnumUpperCase,
  CarFileViewTypeEnum,
  DisplayModeEnum,
  PortalsToggleFieldEnum,
} from 'config/enums'
import * as routes from 'config/routes'

import useSelectedLocationId from 'hooks/use-selected-location-id'
import useStockSelection from 'hooks/use-stock-selection'

import LoadingIndicator from 'components/atoms/loading-indicator'
import SearchResultsOverview from 'components/layouts/search-results-overview'
import CarShareUploadButton from 'components/molecules/car-share-upload-button'
import Filters from 'components/molecules/filters'
import Overlay from 'components/molecules/overlay'
import StockHeader from 'components/organisms/stock-header'
import StockItems from 'components/organisms/stock-items'
import StockSortOptions from 'components/molecules/stock-sort-options'
import Text from 'components/atoms/text'
import { storageKeys } from 'config/storage'
import storage from 'utilities/storage'
import { ThemeContext } from 'styled-components'
import { useMediaLayout } from 'use-media'
import Typography from 'components/molecules/typography'
import Button from 'components/atoms/button'
import MarktplaatsBulkActionsDialog from './marktplaats-bulk-actions-dialog'
import NewDropdownSelect from 'components/molecules/new-dropdown-select'
import AdvertisementBulkActionsDialog from './advertisement-bulk-actions-dialog'
import CarSelectToolbar from 'components/organisms/car-select-toolbar'

const ResultsContainer = styled.div`
  align-items: start;
  display: grid;
  column-gap: ${({ theme }) => theme.widths.gutter};
  margin-bottom: 40px;

  ${media.desktop`
    grid-template-areas: "spacing header"
                   "filters results";
    grid-template-rows: auto auto;
    grid-template-columns: ${({ theme }) =>
      theme.widths.navigationBarWidth} 1fr;
  `};
`

const StyledStockHeader = styled(StockHeader)`
  font-size: ${({ theme }) => theme.baseFontSize};
  width: 100%;
  display: flex;
  flex-direction: row;

  > div {
    display: flex;
    align-items: center;
  }
`

const StyledSortOptions = styled.div`
  background: ${({ theme }) => theme.colors.sectionBackground};
  margin-bottom: ${({ theme }) => theme.sizings.lvl2};
  min-height: ${({ theme }) => theme.sizings.lvl7};
  min-width: ${({ theme }) =>
    theme.useStyledDropdowns ? 'auto' : theme.sizeByFactor(26.5)};
  max-width: ${({ theme }) =>
    theme.useStyledDropdowns ? theme.sizeByFactor(25) : 'none'};
  width: 100%;
  ${({ theme }) =>
    !theme.useStyledDropdowns &&
    `
      background-image: url('data:image/svg+xml;charset=utf8,%3Csvg%20height%3D%225%22%20preserveAspectRatio%3D%22xMidYMid%20meet%22%20viewBox%3D%220%200%2010%205%22%20width%3D%2224%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22%23999%22%20stroke%3D%22%23999%22%20class%3D%22StyledSvg-sc-11vgbka-2%20ejApbw%22%3E%0A%20%20%20%20%3Cg%20stroke%3D%22none%22%3E%0A%20%20%20%20%20%20%3Cpath%20d%3D%22M0%200l5%205%205-5z%22%3E%3C%2Fpath%3E%0A%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E');
      background-position: right;
      background-repeat: no-repeat;
    `}
  border-bottom: ${({ theme }) =>
    theme.useStyledDropdowns
      ? 'none'
      : `1px solid ${theme.brandCharlie}`};
  border-radius: ${({ theme }) =>
    theme.useStyledDropdowns ? `${theme.cardBorderRadius || 0}px` : '0'};
  display: block;

  > div {
    display: block;
    padding: ${({ theme }) => theme.sizings.lvl1};

    p {
      padding-bottom: ${({ theme }) => theme.sizings.lvl0};
      font-size: ${({ theme }) => theme.smallFontSize};
      font-weight: 400;
      color: ${({ theme }) => theme.brandCharlie};
    }

    select {
      border: none;
      height: auto;
      background-image: none;
    }
  }
`

const StyledOverlay = styled(Overlay)`
  .child-container {
    display: flex;
    flex-direction: column;

    h4 {
      padding-top: ${({ theme }) => theme.sizings.lvl2};
      padding-left: ${({ theme }) => theme.sizings.lvl1};
    }
  }
`

//
// @TODO Refactor RdwFiklters component to regular Filters component when
// it is reusable for both views
const StyledFilters = styled(Filters)`
  display: ${({ filtersVisibleOnMobile }) =>
    filtersVisibleOnMobile ? 'block' : 'none'};
  bottom: 0;
  position: ${({ filtersVisibleOnMobile }) =>
    filtersVisibleOnMobile ? 'fixed' : 'static'};
  left: 0;
  right: 0;
  top: 0;
  z-index: 999;

  ${media.desktop`
    bottom: auto;
    display: block;
    grid-area: filters;
    left: auto;
    position: static;
    right: auto;
    top: auto;
    z-index: auto;
  `};
`

const MobileFilters = styled(Filters)`
  width: 100%;
`

const StockResultsContainer = styled.div`
  // Visually indicate that the results are loading
  position: relative;
  opacity: ${({ $loading }) => ($loading ? 0.5 : 1)};
  transition: opacity 0.2s ease;
  pointer-events: ${({ $loading }) => ($loading ? 'none' : 'auto')};

  ${media.desktop`
    grid-area: results;
  `};
`

const StyledResultsCount = styled(Text)`
  grid-area: count;
  font-size: ${({ theme }) => theme.baseFontSize};
`

const BulkActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.sizings.lvl2} 0;
  height: 100%;
`

const SelectActionContainer = styled.div`
  display: flex;
  align-items: center;
  height: ${({ theme }) => theme.sizings.lvl2};

  button {
    padding-left: ${({ theme }) => theme.sizeByFactor(1.5)};
    height: 100%;
  }
`

const StyledSelectedCarLabel = styled.div`
  padding-right: ${({ theme }) => theme.sizeByFactor(1.5)};
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  border-right: 1px solid ${({ theme }) => theme.colors.brandDelta};
  height: 100%;
  display: flex;
  align-items: center;
`

const StyledNewDropDownSelect = styled(NewDropdownSelect)`
  width: ${({ theme }) => theme.sizeByFactor(20.5)};

  label {
    color: ${({ theme }) => theme.colors.brandCharlie};
  }

  > div {
    background-color: ${({ theme }) => theme.colors.brandGolf} !important};
  }
`

const SelectionToolbarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  font-size: ${({ theme }) => theme.smallFontSize};
  color: ${({ theme }) => theme.colors.darkBackground};
`

const StyledSelectedCount = styled.div`
  font-weight: ${({ theme }) => theme.baseFontWeight};
`

const SelectAllButton = styled(Button)`
  padding: 0;
  height: auto;
`

const Stock = ({
  getStockSortOptions,
  getStockFilters,
  getStockResults,
  history,
  location,
  toggleSalesView,
  setStockViewType,
  salesView,
  stockViewType,
  stockFilters,
  stockResults,
  stockSortOptions,
}) => {
  const { t } = useTranslation()
  const theme = useContext(ThemeContext)
  const isDesktop = useMediaLayout({ minWidth: theme.metrics.desktop })
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const [showBulkActionsDialog, setShowBulkActionsDialog] = useState(false)
  const [
    showAdvertisementBulkActionsDialog,
    setShowAdvertisementBulkActionsDialog,
  ] = useState(false)
  const [advertisementPortal, setAdvertisementPortal] = useState(null)
  const [selectedPortal, setSelectedPortal] = useState(null)
  const [selectedPortalName, setSelectedPortalName] = useState(null)
  const bulkActionLabel = t('marktplaatsForm.formLabels.bulkActions')
  const [currentDisplayMode, setCurrentDisplayMode] = useState(
    isDesktop
      ? storage.getPermanent(storageKeys.STOCK_DISPLAY_MODE) ||
          DisplayModeEnum.grid
      : DisplayModeEnum.list,
  )

  useEffect(() => {
    if (!isDesktop) {
      setCurrentDisplayMode(DisplayModeEnum.list)
    } else {
      setCurrentDisplayMode(
        storage.getPermanent(storageKeys.STOCK_DISPLAY_MODE) ||
          DisplayModeEnum.grid,
      )
    }
  }, [isDesktop])

  const routeLocation = useLocation()
  const allParams = getObjectFromQueryString(routeLocation.search)
  const selectedViewType =
    allParams.view === CarFileViewTypeEnum.ADS
      ? CarFileViewTypeEnum.ADVERTISEMENT
      : allParams.view
  const advertisementView =
    selectedViewType === CarFileViewTypeEnum.ADVERTISEMENT
  const marktplaatsView = selectedViewType === CarFileViewTypeEnum.MARKTPLAATS

  // Clear any previously saved stock URL on mount
  useEffect(() => {
    storage.removePermanent(storageKeys.LAST_STOCK_URL)
  }, [])

  // Track the current stock URL in a ref so the unmount cleanup has the latest value
  const lastStockUrlRef = useRef()
  useEffect(() => {
    lastStockUrlRef.current = `${routeLocation.pathname}${routeLocation.search}`
  }, [routeLocation.pathname, routeLocation.search])

  // Save the stock URL to localStorage when leaving the page
  useEffect(() => {
    return () => {
      if (lastStockUrlRef.current) {
        storage.setPermanent(
          storageKeys.LAST_STOCK_URL,
          lastStockUrlRef.current,
        )
      }
    }
  }, [])

  // We need to sync the stock page with carfile as now in the stock page we have a dropdown with more options instead of a toggle switch
  useEffect(() => {
    const params = getObjectFromQueryString(routeLocation.search)
    const urlView = params.view
    if (urlView) {
      if (urlView !== stockViewType) {
        setStockViewType(urlView)
      }
    } else if (stockViewType) {
      const searchParams = new URLSearchParams(routeLocation.search)
      searchParams.set('view', stockViewType)
      history.replace({
        pathname: routeLocation.pathname,
        search: `?${searchParams.toString()}`,
      })
    }
  }, [
    routeLocation.search,
    routeLocation.pathname,
    stockViewType,
    setStockViewType,
    history,
  ])

  const portalFilter = stockFilters?.data?.filter(
    (item) => item.type === 'toggleCheckbox',
  )[0]
  const portals = portalFilter?.items || []

  const handleBulkAction = () => {
    setShowBulkActionsDialog(true)
  }

  const setDisplayMode = (mode) => {
    storage.setPermanent(storageKeys.STOCK_DISPLAY_MODE, mode)
    setCurrentDisplayMode(mode)
  }
  const carShareUploadEnabled = useSelector(
    (state) => state?.auth?.userDetails?.carshare_import,
  )
  const instanceId = useSelector((state) => state.auth.instanceId)

  // Redirect to ?status=binnen if no status is set
  const isInitialized = useRef(false)

  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true
      const searchParams = new URLSearchParams(location.search)
      const status = searchParams.get('status')
      if (!status) {
        searchParams.set(
          'status',
          CarFileStatusEnumLowerCase[CarFileStatusEnumUpperCase.binnen],
        )
        history.replace(`${routes.STOCK}?${searchParams.toString()}`)
      }
    }

    return () => {
      isInitialized.current = false
    }
  }, [location.search, history])

  // Sorting
  const parseSortOptionFromQuery = (query) => {
    const params = new URLSearchParams(query)
    return {
      sort: params.get('sort'),
      dir: params.get('dir'),
    }
  }
  const getSortOptionValuesFromQuery = useCallback(() => {
    return parseSortOptionFromQuery(location.search)
  }, [location.search])

  const sortOptions =
    stockSortOptions &&
    stockSortOptions.data &&
    stockSortOptions.data.length &&
    stockSortOptions.data.map((item) => ({
      ...item,
      label: `${item.label} (${t(item.dir)})`,
      value: `sort=${item.field}&dir=${item.dir}`,
    }))
  const defaultSelectedSortOption =
    sortOptions && sortOptions.find((option) => option.selected)

  const sortOptionValuesFromQuery = getSortOptionValuesFromQuery()
  const selectedSortOption =
    sortOptions &&
    sortOptions.find(
      (option) =>
        option.field === sortOptionValuesFromQuery.sort &&
        option.dir === sortOptionValuesFromQuery.dir,
    )
  const sortOption = selectedSortOption || defaultSelectedSortOption
  const sortOptionQuery =
    sortOption && `sort=${sortOption.field}&dir=${sortOption.dir}`
  // /Sorting

  const queryFilters = getObjectFromQueryString(location.search)
  if (!queryFilters.status) {
    queryFilters.status =
      CarFileStatusEnumLowerCase[CarFileStatusEnumUpperCase.binnen]
  }
  const { status, vestiging_id: vestigingId } = queryFilters

  const {
    selectedCarIds,
    isLoadingAllCars,
    unpublishedCarIds,
    handleSelectCar,
    handleSelectAll,
  } = useStockSelection({
    stockResults,
    queryFilters,
    sortOption,
    instanceId,
    selectedViewType,
  })

  // Update the selected location ID
  const normalizedVestigingId = vestigingId || '0'
  // eslint-disable-next-line no-unused-vars
  const [_, setSelectedLocationId] = useSelectedLocationId(
    normalizedVestigingId,
  )
  useEffect(() => {
    setSelectedLocationId(normalizedVestigingId)
  }, [normalizedVestigingId, setSelectedLocationId])

  const loading = !stockFilters || !stockFilters.data || !sortOption
  const stockResultsTotal =
    stockResults && stockResults.data && stockResults.data.total

  const resultsText = Number(stockResultsTotal)
    ? t('numberOfResults', { count: Number(stockResultsTotal) })
    : t('noResults')

  // Only exclude filter-selected portals from the "Adverteren op" dropdown when the toggle is "wel" (on),
  // since those cars are already advertised on those portals. When "niet" (off), keep all portals available.
  const isPortalToggleOn =
    allParams[portalFilter?.toggle_field] === PortalsToggleFieldEnum.ON
  const selectedPortalFilterIds =
    isPortalToggleOn && portalFilter?.id
      ? [].concat(allParams[portalFilter.id] || []).map(String)
      : []
  const availablePortals = selectedPortalFilterIds.length
    ? portals.filter(
        (portal) => !selectedPortalFilterIds.includes(String(portal.value)),
      )
    : portals

  // determine which page we are one from query string:
  const page = queryFilters && (queryFilters.page || 1)
  const resultsPerPage = 36

  const handleResetFilters = () => {
    history.push(
      `${routes.STOCK}?status=${
        CarFileStatusEnumLowerCase[CarFileStatusEnumUpperCase.binnen]
      }`,
    )
    setMobileFiltersOpen(false)
  }

  const handleFilterChange = (filter, value) => {
    handleChangeQuery(location, history, filter, value, filter !== 'page', true)
  }

  const handleSortOptionChange = (value) => {
    const values = parseSortOptionFromQuery(value)
    const newQuery = new URLSearchParams(location.search)
    newQuery.set('sort', values.sort)
    newQuery.set('dir', values.dir)
    history.push({
      pathname: location.pathname,
      search: newQuery.toString(),
    })
  }

  const handleToggleMobileFilters = () => {
    setMobileFiltersOpen(!mobileFiltersOpen)
  }

  const handleChangePage = (newPage) => {
    handleFilterChange('page', newPage)
    // scroll to top because that's were the new results will be:
    scrollToTop()
  }

  const handleBulkAdvertisement = (value) => {
    setShowAdvertisementBulkActionsDialog(true)
    setSelectedPortal(value)
    setAdvertisementPortal(value)
    setSelectedPortalName(
      portals.find((p) => String(p.value) === String(value))?.label ?? value,
    )
  }

  const handleDialogClose = () => {
    setShowAdvertisementBulkActionsDialog(false)
    setAdvertisementPortal(null)
  }

  useEffect(() => {
    if (status) {
      getStockSortOptions({ status })
    }
  }, [status, getStockSortOptions])

  useDeepCompareEffect(() => {
    if (!sortOption || !status) {
      return
    }

    const filters = {
      ...queryFilters,
      dir: sortOption.dir,
      sort: sortOption.field,
    }
    getStockFilters(filters)
    getStockResults(filters, page, resultsPerPage)
  }, [queryFilters, getStockResults, page, resultsPerPage, sortOption])

  const linkGenerator = (item) => {
    const carId = item.id || item.auto_id

    // When this item is a CarShare car, when a UCC user offers for bids it in their own group, link to the carshare carfile url
    if (item.is_carshare) {
      return `${routes.CAR_SHARE_CAR_FILE}/${carId}/${item.carshare_vestiging_id}`
    }

    if (!carId) {
      return `${routes.CAR_ENTRY}/1${
        routes.LICENSE_PLATE_SEARCH_BY_LICENSE
      }?kenteken=${item.kenteken}&mileage=${item.km || item.rdwkm}`
    }

    if (advertisementView) {
      return `${routes.CAR_FILE}/${carId}/advertisement/portals`
    }
    if (marktplaatsView) {
      return `${routes.CAR_FILE}/${carId}/advertisement/marktplaats`
    }

    return `${routes.CAR_FILE}/${carId}`
  }

  return (
    <>
      <SearchResultsOverview
        pageTitle={`${t('stock')} ${t(`carFileStatussesV3.${status}`)}`}
        mainTitle={t('stock')}
        statusTitle={t(`carFileStatussesV3.${status}`)}
        pageHeaderOptionsSlot={
          carShareUploadEnabled ? <CarShareUploadButton /> : null
        }
        totalResults={() => <StyledResultsCount type="h4" text={resultsText} />}
        stockHeader={() => (
          <StyledStockHeader
            onSortOptionChange={handleSortOptionChange}
            salesView={salesView}
            stockViewType={stockViewType}
            setStockViewType={setStockViewType}
            selectedSortOption={sortOptionQuery}
            sortOptions={sortOptions}
            toggleMobileFilters={handleToggleMobileFilters}
            toggleSalesView={toggleSalesView}
            total={Number(stockResultsTotal)}
            displayMode={currentDisplayMode}
            setDisplayMode={setDisplayMode}
            showViewTypeDropdown
          />
        )}
      >
        {loading && <LoadingIndicator />}
        {!loading &&
          stockResults &&
          stockResults.data &&
          stockResults.data.items && (
            <>
              <ResultsContainer>
                {sortOptions && isDesktop && (
                  <StyledSortOptions>
                    <StockSortOptions
                      items={sortOptions}
                      onSortOptionChange={handleSortOptionChange}
                      selectedSortOption={sortOption?.value}
                    />
                  </StyledSortOptions>
                )}
                {isDesktop && (marktplaatsView || advertisementView) && (
                  <BulkActionsContainer>
                    <SelectActionContainer>
                      <StyledSelectedCarLabel>
                        {t('marktplaatsView.selected', {
                          count: selectedCarIds.length,
                        })}
                      </StyledSelectedCarLabel>
                      <Button
                        level="option"
                        text={
                          selectedCarIds.length
                            ? t('deselectAll')
                            : t('selectAll')
                        }
                        onClick={handleSelectAll}
                      />
                    </SelectActionContainer>
                    {advertisementView ? (
                      selectedCarIds.length > 0 && (
                        <StyledNewDropDownSelect
                          filled
                          disabled={selectedCarIds.length === 0}
                          value={advertisementPortal}
                          label="Adverteren op"
                          onChange={handleBulkAdvertisement}
                          items={availablePortals}
                        />
                      )
                    ) : (
                      <div>
                        <Button
                          level="option"
                          text={bulkActionLabel}
                          onClick={handleBulkAction}
                        />
                      </div>
                    )}
                  </BulkActionsContainer>
                )}

                <StyledFilters
                  onUpdate={handleFilterChange}
                  selectedFilters={queryFilters}
                  filters={stockFilters.data}
                  onResetFilters={handleResetFilters}
                />
                <StockResultsContainer
                  $loading={stockResults?.loading || isLoadingAllCars}
                >
                  <StockItems
                    linkGenerator={linkGenerator}
                    items={
                      stockResults &&
                      stockResults.data &&
                      stockResults.data.items
                    }
                    onChangePage={handleChangePage}
                    page={Number(page)}
                    itemsPerPage={resultsPerPage}
                    salesView={salesView}
                    status={status}
                    totalItems={
                      stockResults &&
                      stockResults.data &&
                      stockResults.data.total
                    }
                    displayMode={currentDisplayMode}
                    data-test-e2e="stock-items"
                    selectedCarIds={selectedCarIds}
                    onSelectCar={
                      (marktplaatsView || advertisementView) && handleSelectCar
                    }
                    showCheckbox={
                      isDesktop && (marktplaatsView || advertisementView)
                    }
                  />
                </StockResultsContainer>
              </ResultsContainer>
            </>
          )}
        {/* Car select toolbar for mobile */}
        {!isDesktop && (marktplaatsView || advertisementView) && (
          <CarSelectToolbar>
            <SelectionToolbarContainer>
              <StyledSelectedCount>
                {t('marktplaatsView.selected', {
                  count: selectedCarIds.length,
                })}
              </StyledSelectedCount>

              <SelectAllButton
                level="option"
                text={selectedCarIds.length ? t('deselectAll') : t('selectAll')}
                onClick={handleSelectAll}
                size="small"
              />
            </SelectionToolbarContainer>
            {selectedViewType === CarFileViewTypeEnum.MARKTPLAATS && (
              <Button
                level="option"
                text={bulkActionLabel}
                onClick={handleBulkAction}
              />
            )}
            {selectedViewType === CarFileViewTypeEnum.ADVERTISEMENT && (
              <StyledNewDropDownSelect
                filled
                disabled={selectedCarIds.length === 0}
                value={advertisementPortal}
                label="Adverteren op"
                onChange={handleBulkAdvertisement}
                items={availablePortals}
              />
            )}
          </CarSelectToolbar>
        )}
      </SearchResultsOverview>
      {mobileFiltersOpen && (
        <StyledOverlay
          close={handleToggleMobileFilters}
          title={t('filters')}
          data-test-e2e="mobile-filters"
        >
          {sortOptions && (
            <>
              <Typography type="Level4Heading">{t('sortBy')}</Typography>
              <StyledSortOptions>
                <StockSortOptions
                  items={sortOptions}
                  onSortOptionChange={handleSortOptionChange}
                  selectedSortOption={sortOption?.value}
                />
              </StyledSortOptions>
            </>
          )}
          <MobileFilters
            onUpdate={handleFilterChange}
            selectedFilters={queryFilters}
            filters={stockFilters.data}
            onResetFilters={handleResetFilters}
          />
        </StyledOverlay>
      )}
      {showBulkActionsDialog && (
        <MarktplaatsBulkActionsDialog
          preselectedLocationId={normalizedVestigingId}
          onClose={() => setShowBulkActionsDialog(false)}
          open
          selectedCarIds={selectedCarIds}
          unpublishedCarIds={unpublishedCarIds}
        />
      )}
      {showAdvertisementBulkActionsDialog && (
        <AdvertisementBulkActionsDialog
          onClose={handleDialogClose}
          open
          selectedCarIds={selectedCarIds}
          selectedPortal={selectedPortal}
          selectedPortalName={selectedPortalName}
          selectedCar={
            stockResults?.data?.items?.find(
              (item) => Number(item.id) === Number(selectedCarIds[0]),
            ) || null
          }
        />
      )}
    </>
  )
}

Stock.propTypes = {
  getStockFilters: PropTypes.func.isRequired,
  getStockSortOptions: PropTypes.func.isRequired,
  getStockResults: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  salesView: PropTypes.bool,
  stockFilters: PropTypes.object,
  stockResults: PropTypes.object,
  stockSortOptions: PropTypes.object,
  stockViewType: PropTypes.string,
  toggleSalesView: PropTypes.func,
  setStockViewType: PropTypes.func,
}

Stock.defaultProps = {
  salesView: false,
  stockFilters: null,
  stockResults: null,
  stockSortOptions: null,
  stockViewType: null,
  toggleSalesView: null,
  setStockViewType: null,
}

export default withRouter(Stock)
